import React, { useCallback, useEffect, useState } from "react";
import { FrequencyDetail, Plan, SubscriptionFeature, SubscriptionFrequencies } from "../types/membership/membershipTypes";
import Tabs, { TabButton } from "../components/tabs/Tabs";
import Badge from "../components/badges/Badge";
import { loadScript, toValidNumber } from "../utils/helpers";
import CheckoutMembership from "../components/membership/CheckoutMembership";
import PricingSkeleton from "../skeletons/PricingSkeleton";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux-stores/store";
import { useDispatch } from "react-redux";
import { fetchPricingData } from "../redux-stores/slices/pricingSlice";
import { setBGWhite, setPageTitle } from "../redux-stores/slices/layoutSlice";
import { changeMembership, startMembership, verifyMembershipPayment } from "../services/api/membership";
import { fetchMembershipData } from "../redux-stores/slices/membershipSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RAZORPAY_SDK } from "../constants/constants";
import { analyticsLog } from "../contexts/FirebaseContext";


const Pricing: React.FC = () => {

    const dispatch: AppDispatch = useDispatch();

    const { data: pricing, loading } = useSelector((state: RootState) => state.pricing);
    const [frequency, setFrequency] = useState<SubscriptionFrequencies>(SubscriptionFrequencies.MONTHLY);
    const [durationWisePricing, setDurationWisePricing] = React.useState<{ plan: Plan, frequency: SubscriptionFrequencies } | null>(null);
    const [checkout, setCheckout] = useState<Plan | null>(null);
    const [isProcessing, setProcessing] = useState<boolean>(false);


    const navigate = useNavigate();


    const fetchPricing = useCallback(async (force = false) => {
        dispatch(fetchPricingData(force));
    }, [dispatch])

    useEffect(() => {
        if (durationWisePricing) {
            dispatch(setBGWhite(true));
        } else {
            dispatch(setBGWhite(false));
        }
    }, [durationWisePricing])


    useEffect(() => {
        fetchPricing();
    }, [fetchPricing])

    const getFrequenciesDataAsTabButtons = () => {
        let buttons: TabButton[] = [];
        if (pricing?.frequencies) {
            for (let [id, content] of Object.entries(pricing.frequencies)) {
                buttons.push({ id, content })
            }
        }
        return buttons;
    }

    const getSortedFrequenciesByPrice = (plan: Plan) => {
        if (!plan.frequencies) return [];

        const frequenciesArray: (FrequencyDetail & { slug: string })[] = [];

        for (let [freq, details] of Object.entries(plan.frequencies)) {
            frequenciesArray.push({ ...details, slug: freq });
        }

        return frequenciesArray.sort((a, b) => Number(a.amount) - Number(b.amount));

    }

    const verify = async (paymentUUID: string, response: any) => {
        const status = await verifyMembershipPayment(paymentUUID, response.razorpay_payment_id, response.razorpay_signature);
        if (status) {
            analyticsLog('membership_purchase')
            await dispatch(fetchMembershipData(true));
            await dispatch(fetchPricingData(true));
            navigate('/membership');
        } else {
            toast.error("Payment could not be verified!");
        }
    }

    const checkoutPlan = async () => {

        const plan = durationWisePricing?.plan;
        const frequency = durationWisePricing?.frequency;
        const allowStart = pricing?.allow_start ?? false;
        const activePlanId = pricing?.active_plan?.uuid;

        if (!plan  || !frequency || (!allowStart && !activePlanId)) return;
        setProcessing(true);
        try {
            const loaded = await loadScript(RAZORPAY_SDK);
            if (!loaded) throw new Error("Unable to load Razorpay SDK");

            const checkoutData = allowStart ?
                await startMembership(frequency, plan.id) :
                await changeMembership(frequency, plan.id, activePlanId!);

            const options = {
                key: checkoutData.checkout.key,
                description: checkoutData.checkout.plan_name,
                handler: async (response: any) => {
                    await verify(checkoutData.ump.uuid, response);
                    setProcessing(false);
                },
                modal: {
                    ondismiss: () => {
                        toast.warn("Your payment was cancelled!");
                        setProcessing(false);
                    }
                },
                ...(checkoutData.ump.platform === 'rzp_checkout'
                    ? { order_id: checkoutData.ump.platform_reference.razorpay_order_id }
                    : { subscription_id: checkoutData.ump.platform_reference.razorpay_subscription_id }),
            }

            const rzp = (window as any).Razorpay ? new (window as any).Razorpay(options) : null;
            if (!rzp) throw new Error("Razorpay SDK not loaded properly");

            rzp.open();

            rzp.on('payment.failed', (response: any) => {
                toast.error("Uh Oh! Your payment could not be processed, please try again later");
                setProcessing(false);
            })

        } catch (error) {
            console.log(error);
            setProcessing(false);
        }

    }

    if (loading) return <PricingSkeleton />

    return (
        <div className="pb-4">
            {
                durationWisePricing ? (
                    <>
                        <div className="m-3">
                            <div className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                <button onClick={() => setDurationWisePricing(null)} className="text-dark btn-link"> <i className="fa fa-arrow-left fs-14"></i> </button>
                                <h6 className="m-0">{durationWisePricing?.plan.name}</h6>
                            </div>
                            <p>{durationWisePricing?.plan?.description}</p>
                            {
                                getSortedFrequenciesByPrice(durationWisePricing?.plan)?.map((freq) => (
                                    <div
                                        key={freq.id}
                                        onClick={() => setDurationWisePricing({ plan: durationWisePricing.plan, frequency: freq.slug as SubscriptionFrequencies })}
                                        className={`cursor-pointer p-3 mb-3 rounded-4 position-relative ${freq.slug === durationWisePricing.frequency ? "bg-primary text-white" : "border border-dark"}`}
                                        style={{ borderRadius: "18px" }}
                                    >

                                        {/* Discount Badge */}
                                        {freq.discount > 0 && (
                                            <span
                                                className="position-absolute translate-middle badge bg-success text-white fs-14 py-2"
                                                style={{ borderRadius: "1rem", right: '-25px', }}
                                            >
                                                -{freq.discount}%
                                            </span>
                                        )}

                                        {/* Title */}
                                        <div className="fw-semibold small">{freq.name}</div>

                                        {/* Amount Row */}
                                        <div className="d-flex align-items-center gap-2 mt-1">
                                            <div className="fs-4 fw-bold">₹{freq.amount}</div>

                                            {freq.original_amount && Number(freq.original_amount) > Number(freq.amount) && (
                                                <div className="text-decoration-line-through fs-14">
                                                    ₹{freq.original_amount}
                                                </div>
                                            )}
                                            {
                                                freq.slug !== SubscriptionFrequencies.MONTHLY &&
                                                <div className="fs-14">
                                                    (₹{freq.monthly_amount} / month)
                                                </div>
                                            }
                                        </div>
                                    </div>
                                ))
                            }
                            {
                                (durationWisePricing.plan.id !== pricing?.active_plan?.plan.id || durationWisePricing.frequency !== pricing?.active_plan?.plan_frequency?.frequency_slug) &&
                                <button disabled={isProcessing} onClick={() => checkoutPlan()} style={{ height: '48px' }} className="btn btn-primary rounded-4 bg-primary border-primary w-100 d-flex align-items-center gap-2 justify-content-center">{isProcessing ? 'Please wait...' : 'Buy Now'}</button>
                            }
                        </div>
                        {/* <CheckoutMembership
                            onCheckout={(is_processing) => setProcessing(is_processing)}
                            frequencies={pricing?.frequencies ?? {}}
                            allowStart={pricing?.allow_start ?? false}
                            isActive={checkout ? true : false}
                            activePlanId={pricing?.active_plan?.uuid}
                            plan={durationWisePricing?.plan} frequency={durationWisePricing?.frequency}
                            onClose={() => setCheckout(null)}
                            disableBackdropClick={isProcessing}
                        /> */}
                    </>
                )
                    : (
                        <div className="d-flex flex-column">
                            <h1 className="pop fw-bolder mx-auto">PICK YOUR PLAN</h1>
                            <p className="mx-auto ">Select a plan that fits your needs</p>
                            <div className="p-2">
                                {/* <Tabs buttons={getFrequenciesDataAsTabButtons()} buttonClass="mx-3" onChange={(fq: SubscriptionFrequencies) => setFrequency(fq)} /> */}
                                {
                                    pricing?.plans && pricing.plans.map((plan, i) => {
                                        return (
                                            <div key={i} className={`card card-style py-4 position-relative ${plan.featured ? 'border border-green-dark' : ''}`}>
                                                <div className="px-4">
                                                    {/* <img src="images/undraw/1.svg" width="250" className="mx-auto" /> */}
                                                    <div className="d-flex justify-content-between align-items-center" style={{ minHeight: '3rem' }}>
                                                        <div className="d-flex justify-content-start align-items-center">
                                                            <h1 className="font-40">
                                                                <sup className="price-sup font-22">₹</sup>
                                                                {toValidNumber(plan.frequencies?.[frequency]?.amount)}
                                                            </h1>
                                                            {
                                                                plan.frequencies?.[frequency]?.original_amount !== plan.frequencies?.[frequency]?.amount &&
                                                                <div className="d-flex flex-column ms-2">
                                                                    <span className="fw-semibold font-20 opacity-50 " data-kt-element="period" style={{ textDecoration: "line-through" }}>₹ {plan['frequencies']?.[frequency]?.['original_amount']}</span>
                                                                    <span className="color-highlight">({plan['frequencies']?.[frequency]?.['discount']}% off)</span>
                                                                </div>
                                                            }
                                                        </div>

                                                        {
                                                            plan.id === pricing.active_plan?.plan.id &&
                                                            <Badge variant="highlight">Current Plan</Badge>
                                                        }
                                                    </div>
                                                    <h4 className="font-20 pop mt-2">{plan?.name}</h4>
                                                    <p className="text-gray-500 mb-2">{plan?.description ?? 'A simple Start for Everyone'}</p>

                                                </div>
                                                <div className="divider my-2"></div>
                                                <div className="px-3 my-2">
                                                    {
                                                        pricing.features && pricing.features.map((sf: SubscriptionFeature, i: number) => {
                                                            return (
                                                                <div className="text-gray-800" key={i}>
                                                                    {
                                                                        plan.features?.[sf.id] ? <i className="fa fa-check-circle color-green-dark me-2"></i> : <i className="fa fa-times-circle color-red-dark me-2"></i>
                                                                    }
                                                                    <span>{sf.name}</span>
                                                                    {
                                                                        sf.type === 'consumable' && <span>- <b>{plan.features?.[sf.id]?.value}</b> per month</span>
                                                                    }
                                                                </div>
                                                            )
                                                        })
                                                    }
                                                </div>
                                                <div className="d-flex justify-content-center">
                                                    {/* {
                                                        plan.id === pricing.active_plan?.plan.id && frequency === pricing.active_plan?.plan_frequency?.frequency_slug &&
                                                        <button className={`btn w-100 mx-3 text-center rounded-sm btn-sm ${plan.featured ? 'bg-green-dark' : 'btn-primary'}`}>Current Plan</button>
                                                    } */}
                                                    {
                                                        // (plan.id !== pricing?.active_plan?.plan.id || frequency !== pricing?.active_plan?.plan_frequency?.frequency_slug) &&
                                                        <button onClick={e => setDurationWisePricing({ plan, frequency })} className={`btn w-100 mx-3 text-center rounded-sm btn-sm ${plan.featured ? 'bg-green-dark' : 'btn-primary'}`}>
                                                            {
                                                                plan.id === pricing.active_plan?.plan.id ? "Upgrade Plan" : <>{pricing?.allow_start ? 'Start with ' : 'Change to '} {plan.name}</>
                                                            }
                                                        </button>
                                                    }
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    )
            }
        </div>
    )
}

export default Pricing